"use client";
import { IconCircleCheckFilled, IconSparkles } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { plans } from "@/constants/tier";
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./button";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import Link from "next/link";

const uiContent = {
  en: {
    monthly: "Monthly",
    yearly: "Yearly",
    cta: "Get Started",
    creditsLabel: "15,000 credits / month",
    creditsTagline: "What credits cover:",
    creditItems: [
      { label: "Call recording", rate: "1 cr / min" },
      { label: "Transcription", rate: "5 cr / min" },
      { label: "Call documentation", rate: "50 cr / call" },
      { label: "Lead enrichment", rate: "20 cr / lead" },
      { label: "Workflows > 15s", rate: "2 cr / sec" },
    ],
    topupsHeading: "Need more credits?",
    topups: [
      { name: "AI Credit Top-up", desc: "From €5 · extra AI & documentation credits" },
      { name: "Call Minute Top-up", desc: "From €10 · extra VoIP minutes" },
    ],
    workflowNote: "Workflows ≤ 15s run time are always free.",
    contact: "Need a custom plan?",
    contactLink: "Talk to us →",
  },
  de: {
    monthly: "Monatlich",
    yearly: "Jährlich",
    cta: "Jetzt starten",
    creditsLabel: "15.000 Credits / Monat",
    creditsTagline: "Wofür Credits genutzt werden:",
    creditItems: [
      { label: "Anrufaufzeichnung", rate: "1 Cr / Min" },
      { label: "Transkription", rate: "5 Cr / Min" },
      { label: "Anrufdokumentation", rate: "50 Cr / Anruf" },
      { label: "Lead-Anreicherung", rate: "20 Cr / Lead" },
      { label: "Workflows > 15s", rate: "2 Cr / Sek" },
    ],
    topupsHeading: "Mehr Credits benötigt?",
    topups: [
      { name: "KI-Kredit-Aufladung", desc: "Ab 5 € · zusätzliche KI & Dokumentationskredite" },
      { name: "Anrufminuten-Aufladung", desc: "Ab 10 € · zusätzliche VoIP-Minuten" },
    ],
    workflowNote: "Workflows mit ≤ 15s Laufzeit sind immer kostenlos.",
    contact: "Benötigen Sie einen individuellen Plan?",
    contactLink: "Sprechen Sie uns an →",
  },
};

const enterpriseContent = {
  en: {
    badge: "Enterprise",
    tagline: "For large teams & organisations",
    setup: "€5,000 – €15,000",
    setupLabel: "one-time setup",
    price: "€149",
    priceSub: "/ user / month",
    features: [
      "Everything in Team",
      "Dedicated onboarding & setup",
      "Custom integrations & API configuration",
      "Tailored workflow automation",
      "Custom reporting & dashboards",
      "Dedicated account manager",
      "Priority SLA support",
      "Custom data retention policies",
      "Volume credits included",
    ],
    cta: "Contact Sales",
    note: "Pricing quoted individually based on team size and requirements.",
  },
  de: {
    badge: "Enterprise",
    tagline: "Für große Teams & Organisationen",
    setup: "5.000 € – 15.000 €",
    setupLabel: "einmalige Einrichtung",
    price: "149 €",
    priceSub: "/ Nutzer / Monat",
    features: [
      "Alles aus Team",
      "Dediziertes Onboarding & Setup",
      "Custom-Integrationen & API-Konfiguration",
      "Maßgeschneiderte Workflow-Automatisierung",
      "Custom Reporting & Dashboards",
      "Dedizierter Account Manager",
      "Prioritäts-SLA-Support",
      "Individuelle Datenaufbewahrungsrichtlinien",
      "Inkludierte Volumen-Credits",
    ],
    cta: "Vertrieb kontaktieren",
    note: "Preise werden individuell je nach Teamgröße und Anforderungen kalkuliert.",
  },
};

const planContent = {
  en: {
    solo: {
      name: "Solo",
      tagline: "For individual reps",
      monthly: { price: "€149", priceSub: "/ month" },
      yearly: { price: "€124", priceSub: "/ month · billed €1,490/year", badge: "Save 17%" },
      features: [
        "Browser-based VoIP calling",
        "Call recording & transcription",
        "AI call documentation",
        "Outcome-based workflows",
        "Lead discovery & enrichment",
        "Smart calling lists & filters",
        "Phone scripts during calls",
        "Email outreach & templates",
        "Basic analytics",
      ],
      note: { monthly: "Cancel anytime", yearly: "14-day money-back guarantee" },
    },
    team: {
      name: "Team",
      tagline: "For growing sales teams",
      monthly: { price: "€149", priceSub: "/ seat / month" },
      yearly: { price: "€124", priceSub: "/ seat / month · billed €1,495/seat/year", badge: "Save 17%" },
      features: [
        "Everything in Solo",
        "Unlimited seats",
        "Team management & roles",
        "Role-based permissions",
        "Advanced analytics",
        "Agent leaderboards",
        "Priority support",
      ],
      note: { monthly: "Cancel anytime", yearly: "14-day money-back guarantee" },
    },
  },
  de: {
    solo: {
      name: "Solo",
      tagline: "Für einzelne Vertriebsmitarbeiter",
      monthly: { price: "149 €", priceSub: "/ Monat" },
      yearly: { price: "124 €", priceSub: "/ Monat · 1.490 €/Jahr", badge: "17% sparen" },
      features: [
        "Browser-basiertes VoIP-Telefonieren",
        "Anrufaufzeichnung & Transkription",
        "KI-Anrufdokumentation",
        "Ergebnisbasierte Workflows",
        "Lead-Erkundung & -Anreicherung",
        "Smarte Anruflisten & Filter",
        "Gesprächsleitfäden während Anrufen",
        "E-Mail-Outreach & Vorlagen",
        "Basis-Analysen",
      ],
      note: { monthly: "Jederzeit kündbar", yearly: "14-Tage-Geld-zurück-Garantie" },
    },
    team: {
      name: "Team",
      tagline: "Für wachsende Vertriebsteams",
      monthly: { price: "149 €", priceSub: "/ Seat / Monat" },
      yearly: { price: "124 €", priceSub: "/ Seat / Monat · 1.495 €/Seat/Jahr", badge: "17% sparen" },
      features: [
        "Alles aus Solo",
        "Unbegrenzte Seats",
        "Teamverwaltung & Rollen",
        "Rollenbasierte Berechtigungen",
        "Erweiterte Analysen",
        "Agenten-Ranglisten",
        "Prioritäts-Support",
      ],
      note: { monthly: "Jederzeit kündbar", yearly: "14-Tage-Geld-zurück-Garantie" },
    },
  },
};

export function Pricing() {
  const locale = useLocale();
  const ui = locale === "de" ? uiContent.de : uiContent.en;
  const pc = locale === "de" ? planContent.de : planContent.en;
  const [billing, setBilling] = useState<"monthly" | "yearly">("yearly");
  const router = useRouter();

  const handleBuyClick = (tier: "solo" | "team") => {
    const plan = plans.find((p) => p.tier === tier && p.billing === billing);
    router.push(`/signup?plan=${plan?.id ?? tier}`);
  };

  const renderEnterpriseCard = () => {
    const e = locale === "de" ? enterpriseContent.de : enterpriseContent.en;
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="relative flex flex-col md:flex-row md:items-center gap-8 rounded-2xl p-8 bg-white dark:bg-neutral-900 ring-1 ring-neutral-200 dark:ring-neutral-700 shadow-lg"
      >
        <div className="flex-1">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-neutral-400 mb-1.5">
            {e.tagline}
          </span>
          <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">{e.badge}</h3>
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="text-3xl font-extrabold text-neutral-900 dark:text-white">{e.price}</span>
            <span className="text-sm text-neutral-500">{e.priceSub}</span>
          </div>
          <p className="text-xs text-neutral-400 mt-1">{e.note}</p>
        </div>

        <div className="flex-1">
          <ul className="space-y-2.5 mb-6">
            {e.features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                <IconCircleCheckFilled className="h-4 w-4 flex-shrink-0 mt-0.5 text-green-500" />
                {feature}
              </li>
            ))}
          </ul>
          <Link href={`/${locale}/contact`}>
            <Button className="w-full rounded-full py-3 text-sm font-semibold bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-neutral-100">
              {e.cta}
            </Button>
          </Link>
        </div>
      </motion.div>
    );
  };

  const renderCard = (tier: "solo" | "team") => {
    const p = pc[tier];
    const isTeam = tier === "team";
    const priceInfo = p[billing];
    const badge = "badge" in priceInfo ? priceInfo.badge : undefined;

    return (
      <motion.div
        key={`${tier}-${billing}`}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={cn(
          "relative flex flex-col rounded-2xl p-8",
          isTeam
            ? "bg-[radial-gradient(164.75%_100%_at_50%_0%,#334155_0%,#0F172A_48.73%)] shadow-2xl"
            : "bg-white dark:bg-neutral-900 ring-1 ring-neutral-200 dark:ring-neutral-700 shadow-lg"
        )}
      >
        {badge && (
          <div className="absolute top-4 right-4">
            <span className="text-xs bg-green-500 text-white px-2.5 py-1 rounded-full font-semibold">
              {badge}
            </span>
          </div>
        )}
        {isTeam && (
          <div className="absolute top-4 left-4">
            <span className="text-xs bg-white/15 text-white px-2.5 py-1 rounded-full font-medium">
              Most popular
            </span>
          </div>
        )}

        {/* Header */}
        <div className="mt-2 mb-5">
          <p className={cn("text-xs font-medium uppercase tracking-widest mb-1.5", isTeam ? "text-slate-400" : "text-neutral-400")}>
            {p.tagline}
          </p>
          <h3 className={cn("text-2xl font-bold", isTeam ? "text-white" : "text-neutral-900 dark:text-white")}>
            {p.name}
          </h3>
        </div>

        {/* Price */}
        <div className="mb-6">
          <div className="flex items-baseline gap-1.5">
            <span className={cn("text-5xl font-extrabold tracking-tight", isTeam ? "text-white" : "text-neutral-900 dark:text-white")}>
              {priceInfo.price}
            </span>
          </div>
          <p className={cn("text-sm mt-1", isTeam ? "text-slate-400" : "text-neutral-500")}>
            {priceInfo.priceSub}
          </p>
        </div>

        {/* Credits callout */}
        <div className={cn("rounded-xl p-3.5 mb-6", isTeam ? "bg-white/8 border border-white/10" : "bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700")}>
          <div className="flex items-center gap-2 mb-2.5">
            <IconSparkles className={cn("w-3.5 h-3.5 flex-shrink-0", isTeam ? "text-yellow-400" : "text-yellow-500")} />
            <span className={cn("text-xs font-semibold", isTeam ? "text-white" : "text-neutral-800 dark:text-neutral-200")}>
              {ui.creditsLabel}
            </span>
          </div>
          <div className="space-y-1.5">
            {ui.creditItems.map((item) => (
              <div key={item.label} className="flex items-center justify-between gap-2">
                <span className={cn("text-xs", isTeam ? "text-slate-400" : "text-neutral-500")}>
                  {item.label}
                </span>
                <span className={cn("text-xs font-mono font-medium whitespace-nowrap", isTeam ? "text-slate-300" : "text-neutral-700 dark:text-neutral-300")}>
                  {item.rate}
                </span>
              </div>
            ))}
          </div>
          <p className={cn("text-xs mt-2 pt-2 border-t", isTeam ? "text-slate-500 border-white/10" : "text-neutral-400 border-neutral-200 dark:border-neutral-700")}>
            {ui.workflowNote}
          </p>
        </div>

        {/* CTA */}
        <Button
          onClick={() => handleBuyClick(tier)}
          className={cn(
            "w-full rounded-full py-3 text-sm font-semibold mb-6",
            isTeam
              ? "bg-white text-black hover:bg-neutral-100"
              : "bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-white dark:text-black dark:hover:bg-neutral-100"
          )}
        >
          {ui.cta}
        </Button>

        {/* Divider */}
        <div className={cn("h-px mb-5", isTeam ? "bg-white/10" : "bg-neutral-100 dark:bg-neutral-800")} />

        {/* Features */}
        <ul className="space-y-3 flex-1">
          {p.features.map((feature) => (
            <li key={feature} className={cn("flex items-start gap-3 text-sm", isTeam ? "text-slate-300" : "text-neutral-700 dark:text-neutral-300")}>
              <IconCircleCheckFilled className={cn("h-4 w-4 flex-shrink-0 mt-0.5", isTeam ? "text-green-400" : "text-green-500")} />
              {feature}
            </li>
          ))}
        </ul>

        {/* Footer note */}
        <p className={cn("text-center text-xs mt-6 pt-5 border-t", isTeam ? "text-slate-500 border-white/10" : "text-neutral-400 border-neutral-100 dark:border-neutral-800")}>
          {p.note[billing]}
        </p>
      </motion.div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Billing toggle */}
      <div className="flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 w-fit mx-auto mb-10 rounded-full p-1">
        {(["monthly", "yearly"] as const).map((cycle) => (
          <button
            key={cycle}
            className={cn(
              "text-sm font-medium px-6 py-2.5 rounded-full relative transition-colors",
              billing === cycle ? "text-white dark:text-black" : "text-gray-500 dark:text-muted-dark hover:text-gray-700"
            )}
            onClick={() => setBilling(cycle)}
          >
            {billing === cycle && (
              <motion.span
                layoutId="pricing-toggle"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                className="absolute inset-0 bg-black dark:bg-white rounded-full"
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              {cycle === "monthly" ? ui.monthly : ui.yearly}
              {cycle === "yearly" && (
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                  {locale === "de" ? "Sparen" : "Save"}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-4">
        {renderCard("solo")}
        {renderCard("team")}
      </div>

      {/* Enterprise card */}
      <div className="mt-5 px-4">
        {renderEnterpriseCard()}
      </div>

      {/* Add-ons */}
      <div className="mt-10 px-4">
        <p className="text-center text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-4">
          {ui.topupsHeading}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ui.topups.map((topup) => (
            <div
              key={topup.name}
              className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800"
            >
              <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{topup.name}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{topup.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
